/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetLeaderboardRow, GetLeaderboardRankRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String, HELPERS } from '../../helpers/index.js';
import { fromBinary } from '@bufbuild/protobuf';
import { decodeRealtimePayload } from '../../devtool/decode-realtime-payload.js';
import { LeaderboardRowSchema } from '@morfi/proto';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';
import type { PagedData } from '../../types/PagedData.js';


// ─── useGetLeaderboard ────────────────────────────────────────────────────────────

export interface useGetLeaderboardArgs { readonly kind: string; readonly sortBy?: string; readonly sortOrder?: string; readonly window?: string; readonly anchorUserId?: string; readonly viewportGated?: boolean; readonly fields?: readonly ('rowId' | 'totalPnl' | 'realizedPnl' | 'totalVolume' | 'totalTrades' | 'marketsTraded' | 'pnlRank' | 'volumeRank' | 'tradesRank' | 'marketsRank' | 'totalAmountBoosted' | 'numBoosts' | 'marketsBoosted' | 'amountRank' | 'boostsRank' | 'marketsBoostedRank' | 'topMarketPubkey' | 'topMarketTitle' | 'topMarketVolume' | 'topMarketRank' | 'totalMarketsCreated' | 'creatorTotalVolume' | 'marketsCreatedRank' | 'totalVolumeRank' | 'totalBackedNetD6' | 'numBacks' | 'backsRank' | 'user')[]; }


async function _useGetLeaderboardQueryFn(
  args: useGetLeaderboardArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetLeaderboardRow>> {
  const url = `/v1/leaderboards/${args.kind}`;
  const params = new URLSearchParams();

  if (args.sortBy !== undefined) params.set('sortBy', String(args.sortBy));
  if (args.sortOrder !== undefined) params.set('sortOrder', String(args.sortOrder));
  if (args.window !== undefined) params.set('window', String(args.window));
  if (args.anchorUserId !== undefined) params.set('anchorUserId', String(args.anchorUserId));
  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetLeaderboard',
    routeTemplate: '/v1/leaderboards/{kind}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetLeaderboardRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetLeaderboardFactory = Object.assign(
  (args: useGetLeaderboardArgs) => ({
    queryKey: ['leaderboards', 'list', args.kind, { sortBy: args.sortBy ?? null, sortOrder: args.sortOrder ?? null, window: args.window ?? null, anchorUserId: args.anchorUserId ?? null }, ((args.fields === undefined ? undefined : [...new Set([...args.fields, 'rowId'])].sort()) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetLeaderboardQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetLeaderboard',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['leaderboards', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetLeaderboardArgs, PagedData<GetLeaderboardRow>>;


export const useGetLeaderboardSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/leaderboards/{kind}',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetLeaderboardFactory },
  parseInput: (raw: unknown): useGetLeaderboardArgs => raw as useGetLeaderboardArgs,
  parseResponse: (raw: unknown): PagedData<GetLeaderboardRow> => raw as PagedData<GetLeaderboardRow>,
  specName: 'useGetLeaderboard' as const,
  polling: { hasNats: true, intervalMs: 15000 } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetLeaderboardRow>>;
export const useGetLeaderboardPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetLeaderboardArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetLeaderboardQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetLeaderboardPerEntitySubjects(args: useGetLeaderboardArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [`morfi.leaderboard.${args.kind}.${args.window}.row.v1`];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetLeaderboardRowSubjectTemplates(args: useGetLeaderboardArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [`morfi.leaderboard.${args.kind}.${args.window}.row.v1`];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetLeaderboardApplyEvent(
  prev: PagedData<GetLeaderboardRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetLeaderboardArgs,
): PagedData<GetLeaderboardRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // applyEvent · morfi.leaderboard.${kind}.${window}.row.v1
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.leaderboard\.([^.]+)\.([^.]+)\.row\.v1$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(LeaderboardRowSchema, _d)
            : fromBinary(LeaderboardRowSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof LeaderboardRowSchema>>;
      const _next = HELPERS.applyLeaderboardRow(
        prev,
        raw,
        args as unknown as Record<string, unknown>,
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetLeaderboardRealtime = {
  domain: 'leaderboards' as const,
  domainPins: ['morfi.leaderboard.*.*.row.v1'] as readonly string[],
  rowKey: 'rowId' as const,
  perEntitySubjects: (args: useGetLeaderboardArgs) => _useGetLeaderboardPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetLeaderboardArgs) => _useGetLeaderboardRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetLeaderboardArgs) =>
    (prev: PagedData<GetLeaderboardRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetLeaderboardRow> | undefined =>
      _useGetLeaderboardApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetLeaderboardRow>, unknown>;


// ─── useGetLeaderboardRank ────────────────────────────────────────────────────────────

export interface useGetLeaderboardRankArgs { readonly kind: string; readonly userId: string; readonly window?: string; readonly viewportGated?: boolean; readonly fields?: readonly ('rowId' | 'totalPnl' | 'realizedPnl' | 'totalVolume' | 'totalTrades' | 'marketsTraded' | 'pnlRank' | 'volumeRank' | 'tradesRank' | 'marketsRank' | 'totalAmountBoosted' | 'numBoosts' | 'marketsBoosted' | 'amountRank' | 'boostsRank' | 'marketsBoostedRank' | 'topMarketPubkey' | 'topMarketTitle' | 'topMarketVolume' | 'topMarketRank' | 'totalMarketsCreated' | 'creatorTotalVolume' | 'marketsCreatedRank' | 'totalVolumeRank' | 'totalBackedNetD6' | 'numBacks' | 'backsRank' | 'user')[]; }


async function _useGetLeaderboardRankQueryFn(
  args: useGetLeaderboardRankArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetLeaderboardRankRow>> {
  const url = `/v1/leaderboards/${args.kind}/rank/${args.userId}`;
  const params = new URLSearchParams();

  if (args.window !== undefined) params.set('window', String(args.window));
  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetLeaderboardRank',
    routeTemplate: '/v1/leaderboards/{kind}/rank/{userId}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetLeaderboardRankRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetLeaderboardRankFactory = Object.assign(
  (args: useGetLeaderboardRankArgs) => ({
    queryKey: ['leaderboards', 'list', args.kind, { window: args.window ?? null }, ((args.fields === undefined ? undefined : [...new Set([...args.fields, 'rowId'])].sort()) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetLeaderboardRankQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetLeaderboardRank',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['leaderboards', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetLeaderboardRankArgs, PagedData<GetLeaderboardRankRow>>;


export const useGetLeaderboardRankSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/leaderboards/{kind}/rank/{userId}',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetLeaderboardRankFactory },
  parseInput: (raw: unknown): useGetLeaderboardRankArgs => raw as useGetLeaderboardRankArgs,
  parseResponse: (raw: unknown): PagedData<GetLeaderboardRankRow> => raw as PagedData<GetLeaderboardRankRow>,
  specName: 'useGetLeaderboardRank' as const,
  polling: { hasNats: true, intervalMs: 15000 } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetLeaderboardRankRow>>;
export const useGetLeaderboardRankPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetLeaderboardRankArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetLeaderboardRankQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetLeaderboardRankPerEntitySubjects(args: useGetLeaderboardRankArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [`morfi.leaderboard.${args.kind}.${args.window}.row.v1`];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetLeaderboardRankRowSubjectTemplates(args: useGetLeaderboardRankArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [`morfi.leaderboard.${args.kind}.${args.window}.row.v1`];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetLeaderboardRankApplyEvent(
  prev: PagedData<GetLeaderboardRankRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetLeaderboardRankArgs,
): PagedData<GetLeaderboardRankRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // applyEvent · morfi.leaderboard.${kind}.${window}.row.v1
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.leaderboard\.([^.]+)\.([^.]+)\.row\.v1$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(LeaderboardRowSchema, _d)
            : fromBinary(LeaderboardRowSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof LeaderboardRowSchema>>;
      const _next = HELPERS.applyLeaderboardRow(
        prev,
        raw,
        args as unknown as Record<string, unknown>,
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetLeaderboardRankRealtime = {
  domain: 'leaderboards' as const,
  domainPins: ['morfi.leaderboard.*.*.row.v1'] as readonly string[],
  rowKey: 'rowId' as const,
  perEntitySubjects: (args: useGetLeaderboardRankArgs) => _useGetLeaderboardRankPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetLeaderboardRankArgs) => _useGetLeaderboardRankRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetLeaderboardRankArgs) =>
    (prev: PagedData<GetLeaderboardRankRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetLeaderboardRankRow> | undefined =>
      _useGetLeaderboardRankApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetLeaderboardRankRow>, unknown>;
